import { z } from 'zod';
import { defineStateMachine } from '../common/state-machine.js';
/** `CreditLine` (credit-core) — máquinas de estado §3. */
export const CreditLineMachine = defineStateMachine({
    name: 'CreditLine',
    states: ['PENDING_ACTIVATION', 'ACTIVE', 'BLOCKED', 'DELINQUENT', 'CLOSED'],
    initial: ['PENDING_ACTIVATION'],
    terminal: ['CLOSED'],
    transitions: {
        PENDING_ACTIVATION: ['ACTIVE'],
        ACTIVE: ['BLOCKED', 'DELINQUENT', 'CLOSED'],
        BLOCKED: ['ACTIVE', 'CLOSED'],
        DELINQUENT: ['ACTIVE', 'BLOCKED'],
        CLOSED: [],
    },
});
export const CreditLineStatusSchema = CreditLineMachine.schema.meta({ id: 'CreditLineStatus' });
/** Motivo de bloqueo. `CUSTOMER_REQUEST` lo levanta el cliente; el resto solo un analista. */
export const BlockReasonSchema = z
    .enum(['CUSTOMER_REQUEST', 'FRAUD', 'RISK', 'DELINQUENCY', 'ADMIN'])
    .meta({ id: 'BlockReason' });
export const CUSTOMER_UNBLOCKABLE_REASONS = ['CUSTOMER_REQUEST'];
/** `CreditAuthorization` (hold) — §2. `ACTIVE → ACTIVE` = extend (hasta 24 h). */
export const CreditAuthorizationMachine = defineStateMachine({
    name: 'CreditAuthorization',
    states: ['ACTIVE', 'DECLINED', 'CAPTURED', 'RELEASED', 'EXPIRED'],
    initial: ['ACTIVE', 'DECLINED'],
    terminal: ['DECLINED', 'CAPTURED', 'RELEASED', 'EXPIRED'],
    transitions: {
        ACTIVE: ['ACTIVE', 'CAPTURED', 'RELEASED', 'EXPIRED'],
        DECLINED: [],
        CAPTURED: [],
        RELEASED: [],
        EXPIRED: [],
    },
});
export const CreditAuthorizationStatusSchema = CreditAuthorizationMachine.schema.meta({
    id: 'CreditAuthorizationStatus',
});
export const MAX_HOLD_HOURS = 24;
/** `Drawdown` (utilización) — §4. */
export const DrawdownMachine = defineStateMachine({
    name: 'Drawdown',
    states: ['ACTIVE', 'IN_DISPUTE', 'PARTIALLY_REVERSED', 'REVERSED', 'PAID_OFF', 'CHARGED_OFF'],
    initial: ['ACTIVE'],
    terminal: ['REVERSED', 'PAID_OFF', 'CHARGED_OFF'],
    transitions: {
        ACTIVE: ['IN_DISPUTE', 'PARTIALLY_REVERSED', 'REVERSED', 'PAID_OFF', 'CHARGED_OFF'],
        IN_DISPUTE: ['ACTIVE', 'PARTIALLY_REVERSED', 'REVERSED'],
        PARTIALLY_REVERSED: ['REVERSED', 'IN_DISPUTE', 'PAID_OFF'],
        REVERSED: [],
        PAID_OFF: [],
        CHARGED_OFF: [],
    },
});
export const DrawdownStatusSchema = DrawdownMachine.schema.meta({ id: 'DrawdownStatus' });
export const InstallmentStatusSchema = z
    .enum(['SCHEDULED', 'BILLED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'REVERSED'])
    .meta({ id: 'InstallmentStatus' });
export const BillingCycleStatusSchema = z.enum(['OPEN', 'CUT', 'CLOSED']);
export const CreditProductStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'RETIRED']);
export const DrawdownEventTypeSchema = z.enum([
    'CAPTURED',
    'DISPUTED',
    'DISPUTE_RESOLVED',
    'REVERSED',
    'REFUND_APPLIED',
    'PREPAID',
    'CHARGED_OFF',
]);
/** Altura de mora por días (`0`, `1–30`, `31–60`, `61–90`, `91–120`, `>120`). */
export const DelinquencyBucketSchema = z
    .enum(['CURRENT', 'DPD_1_30', 'DPD_31_60', 'DPD_61_90', 'DPD_91_120', 'DPD_120_PLUS'])
    .meta({ id: 'DelinquencyBucket' });
export function delinquencyBucket(dpd) {
    if (dpd <= 0)
        return 'CURRENT';
    if (dpd <= 30)
        return 'DPD_1_30';
    if (dpd <= 60)
        return 'DPD_31_60';
    if (dpd <= 90)
        return 'DPD_61_90';
    if (dpd <= 120)
        return 'DPD_91_120';
    return 'DPD_120_PLUS';
}
/** Calificación de riesgo simple por dpd (A–E). */
export const CreditRatingSchema = z.enum(['A', 'B', 'C', 'D', 'E']).meta({ id: 'CreditRating' });
//# sourceMappingURL=states.js.map